var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { prepareZXingModule, readBarcodes } from 'zxing-wasm/reader';
import { extractScreenCodeFromQr } from '../../utils';
import { colors } from '../../styles/colors';
const calculateBrightness = (imageData) => {
    let total = 0;
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        total += (data[i] + data[i + 1] + data[i + 2]) / 3;
    }
    return total / (imageData.width * imageData.height);
};
export const ScreenQrScanner = ({ onCodeDetected, onCancel, onCameraUnavailable, }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [status, setStatus] = useState('Наведите камеру на QR экрана');
    const [error, setError] = useState(null);
    const [facingMode, setFacingMode] = useState('environment');
    const streamRef = useRef(null);
    const rafRef = useRef(null);
    const scanningRef = useRef(true);
    const detectedRef = useRef(false);
    const stopAll = () => {
        var _a;
        scanningRef.current = false;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
    };
    const toggleCamera = () => {
        setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
    };
    useEffect(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas)
            return;
        detectedRef.current = false;
        scanningRef.current = true;
        const start = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            try {
                yield prepareZXingModule();
                stopAll();
                scanningRef.current = true;
                if (!((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia)) {
                    setError('Камера недоступна в этом браузере');
                    onCameraUnavailable === null || onCameraUnavailable === void 0 ? void 0 : onCameraUnavailable();
                    return;
                }
                const stream = yield navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode,
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30, max: 30 },
                    },
                    audio: false,
                });
                streamRef.current = stream;
                video.srcObject = stream;
                yield video.play();
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    setError('Не удалось инициализировать камеру');
                    return;
                }
                let frameSkip = 0;
                const scanLoop = () => __awaiter(void 0, void 0, void 0, function* () {
                    if (!scanningRef.current || detectedRef.current)
                        return;
                    if (!videoRef.current || !canvasRef.current)
                        return;
                    rafRef.current = requestAnimationFrame(scanLoop);
                    frameSkip = (frameSkip + 1) % 2;
                    if (frameSkip !== 0)
                        return;
                    const v = videoRef.current;
                    const c = canvasRef.current;
                    const targetW = 1280;
                    const scale = Math.min(1, targetW / (v.videoWidth || targetW));
                    const w = Math.floor((v.videoWidth || targetW) * scale);
                    const h = Math.floor((v.videoHeight || 720) * scale);
                    c.width = w;
                    c.height = h;
                    ctx.drawImage(v, 0, 0, w, h);
                    const imageData = ctx.getImageData(0, 0, w, h);
                    if (calculateBrightness(imageData) < 35) {
                        setStatus('Слишком темно, улучшите освещение');
                        return;
                    }
                    try {
                        const out = yield readBarcodes(imageData, {
                            tryHarder: true,
                            formats: ['QRCode'],
                            maxNumberOfSymbols: 1,
                        });
                        if (out.length > 0) {
                            const code = extractScreenCodeFromQr(out[0].text);
                            if (code) {
                                detectedRef.current = true;
                                stopAll();
                                onCodeDetected(code);
                            }
                            else {
                                setStatus('QR не похож на код экрана');
                            }
                        }
                    }
                    catch (_b) {
                        // игнор одиночных ошибок кадра
                    }
                });
                setError(null);
                setStatus('Наведите камеру на QR экрана');
                scanLoop();
            }
            catch (err) {
                console.error('ScreenQrScanner camera init error', err);
                const name = err === null || err === void 0 ? void 0 : err.name;
                if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
                    setError('Нет доступа к камере — разрешите или введите код вручную');
                }
                else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
                    setError('Камера не найдена — введите код вручную');
                }
                else {
                    setError('Не удалось запустить камеру — введите код вручную');
                }
                onCameraUnavailable === null || onCameraUnavailable === void 0 ? void 0 : onCameraUnavailable();
            }
        });
        start();
        return () => {
            stopAll();
        };
    }, [facingMode, onCodeDetected, onCameraUnavailable]);
    return (_jsxs("div", Object.assign({ style: { display: 'flex', flexDirection: 'column', gap: 12 } }, { children: [error ? (_jsx(Typography, Object.assign({ sx: { color: colors.error } }, { children: error }))) : (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ style: {
                            position: 'relative',
                            borderRadius: 12,
                            overflow: 'hidden',
                            border: `1px solid ${colors.cardBorder}`,
                            background: '#000',
                        } }, { children: [_jsx("video", { ref: videoRef, playsInline: true, muted: true, style: { width: '100%', display: 'block' } }), _jsx("div", { style: {
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '60%',
                                    aspectRatio: '1 / 1',
                                    border: `2px solid ${colors.primary}`,
                                    borderRadius: 10,
                                    pointerEvents: 'none',
                                } })] })), _jsx("canvas", { ref: canvasRef, style: { display: 'none' } }), _jsx(Typography, Object.assign({ variant: "body2", sx: { color: colors.textSecondary } }, { children: status }))] })), _jsxs("div", Object.assign({ style: { display: 'flex', gap: 8, flexWrap: 'wrap' } }, { children: [!error && (_jsx(Button, Object.assign({ variant: "outlined", onClick: toggleCamera }, { children: "\u0421\u043C\u0435\u043D\u0438\u0442\u044C \u043A\u0430\u043C\u0435\u0440\u0443" }))), _jsx(Button, Object.assign({ variant: "outlined", color: "inherit", onClick: onCancel }, { children: "\u041E\u0442\u043C\u0435\u043D\u0430" }))] }))] })));
};
export default ScreenQrScanner;
